import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Settings,
  Plus,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Edit,
  Trash2,
  Play,
  Loader2,
  KeyRound,
} from 'lucide-react';
import { llmProviderAPI } from '../../services/api/adminApi';
import type { LLMProvider, ProviderType, ProviderStatus } from '../../types/admin';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { LLMProviderDialog } from '../../components/admin/LLMProviderDialog';
import { RotateKeyDialog } from '../../components/admin/RotateKeyDialog';

const LLMConfigPage: React.FC = () => {
  const { t } = useTranslation();
  const [providers, setProviders] = useState<LLMProvider[]>([]);
  const [testingProviders, setTestingProviders] = useState<{ [key: string]: boolean }>({});
  const [testStatus, setTestStatus] = useState<{ [key: string]: 'idle' | 'success' | 'failed' }>(
    {}
  );
  const [testLatency, setTestLatency] = useState<{ [key: string]: number }>({});

  const fetchProviders = async () => {
    try {
      const response = await llmProviderAPI.list();
      setProviders(response.data);
    } catch (err) {
      console.error('Failed to fetch providers:', err);
      toast.error('Failed to load LLM providers');
    }
  };

  useEffect(() => {
    fetchProviders();
  }, []);

  // Dialog states
  const [providerDialogOpen, setProviderDialogOpen] = useState(false);
  const [providerDialogMode, setProviderDialogMode] = useState<'create' | 'edit'>('create');
  const [selectedProvider, setSelectedProvider] = useState<LLMProvider | undefined>(undefined);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [rotateConfirmOpen, setRotateConfirmOpen] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState<LLMProvider | null>(null);
  const [providerToRotate, setProviderToRotate] = useState<LLMProvider | null>(null);
  const [saving, setSaving] = useState(false);

  const getStatusColor = (status: ProviderStatus) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'inactive':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'degraded':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: ProviderStatus) => {
    switch (status) {
      case 'active':
        return <CheckCircle size={16} className='text-green-600' />;
      case 'inactive':
        return <XCircle size={16} className='text-gray-600' />;
      case 'degraded':
        return <AlertTriangle size={16} className='text-yellow-600' />;
      default:
        return null;
    }
  };

  const formatLastUsed = (dateString?: string) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000 / 60);
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff}m ago`;
    const hours = Math.floor(diff / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const formatProviderName = (provider: ProviderType) => {
    switch (provider) {
      case 'openai':
        return 'OpenAI';
      case 'anthropic':
        return 'Anthropic';
      case 'ollama':
        return 'Ollama';
      case 'custom':
        return 'Custom';
      default:
        return provider;
    }
  };

  const handleTestConnection = async (providerId: string) => {
    setTestingProviders(prev => ({ ...prev, [providerId]: true }));
    setTestStatus(prev => ({ ...prev, [providerId]: 'idle' }));

    try {
      const response = await llmProviderAPI.test(providerId);
      const result = response.data;

      if (result.status === 'success') {
        setTestStatus(prev => ({ ...prev, [providerId]: 'success' }));
        setTestLatency(prev => ({ ...prev, [providerId]: result.latency_ms }));
        toast.success(t('llmConfig.connectionSuccess', { latency: result.latency_ms }), {
          description: t('llmConfig.connectedTo', {
            name: providers.find(p => p.id === providerId)?.name,
          }),
        });
      } else {
        setTestStatus(prev => ({ ...prev, [providerId]: 'failed' }));
        toast.error(t('llmConfig.connectionFailed'), {
          description: result.message,
        });
      }
    } catch (_error) {
      setTestStatus(prev => ({ ...prev, [providerId]: 'failed' }));
      toast.error(t('llmConfig.connectionTestFailed'));
    } finally {
      setTestingProviders(prev => ({ ...prev, [providerId]: false }));
    }
  };

  const handleRotateApiKey = async (providerId: string) => {
    const provider = providers.find(p => p.id === providerId);
    if (!provider) return;
    setProviderToRotate(provider);
    setRotateConfirmOpen(true);
  };

  const confirmRotateApiKey = async (newApiKey: string) => {
    if (!providerToRotate) return;

    setSaving(true);
    try {
      await llmProviderAPI.rotateKey(providerToRotate.id, { new_api_key: newApiKey });

      toast.success(t('llmConfig.apiKeyRotated'));

      setRotateConfirmOpen(false);
      setProviderToRotate(null);
      await fetchProviders();
    } catch (_error) {
      toast.error(t('llmConfig.rotateKeyFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProvider = (provider: LLMProvider) => {
    setProviderToDelete(provider);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteProvider = async () => {
    if (!providerToDelete) return;

    setSaving(true);
    try {
      await llmProviderAPI.delete(providerToDelete.id);
      toast.success(t('llmConfig.providerDeleted'));
      setDeleteConfirmOpen(false);
      setProviderToDelete(null);
      await fetchProviders();
    } catch (_error) {
      toast.error(t('llmConfig.deleteFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleModel = (providerId: string, modelId: string) => {
    setProviders(prev =>
      prev.map(p => {
        if (p.id === providerId) {
          return {
            ...p,
            models: p.models.map(m => (m.id === modelId ? { ...m, enabled: !m.enabled } : m)),
          };
        }
        return p;
      })
    );
  };

  const handleAddProvider = () => {
    setSelectedProvider(undefined);
    setProviderDialogMode('create');
    setProviderDialogOpen(true);
  };

  const handleEditProvider = (provider: LLMProvider) => {
    setSelectedProvider(provider);
    setProviderDialogMode('edit');
    setProviderDialogOpen(true);
  };

  const handleSaveProvider = async (providerData: Partial<LLMProvider>) => {
    setSaving(true);
    try {
      if (providerDialogMode === 'create') {
        const createData = {
          name: providerData.name!,
          provider: providerData.provider!,
          api_key: '***', // In real implementation, this would come from the dialog
          endpoint: providerData.endpoint,
          rate_limits: providerData.rate_limits,
        };
        await llmProviderAPI.create(createData);
        toast.success(t('llmConfig.providerCreated'));
      } else {
        const updateData = {
          name: providerData.name,
          endpoint: providerData.endpoint,
          status: providerData.status,
          rate_limits: providerData.rate_limits,
        };
        await llmProviderAPI.update(selectedProvider!.id, updateData);
        toast.success(t('llmConfig.providerUpdated'));
      }
      setProviderDialogOpen(false);
      await fetchProviders();
    } catch (_error) {
      toast.error(t('llmConfig.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const getProviderStats = () => {
    return {
      total: providers.length,
      active: providers.filter(p => p.status === 'active').length,
      default:
        providers.find(p => p.models.some(m => m.default_for?.includes('chat')))?.name || 'None',
    };
  };

  const stats = getProviderStats();

  return (
    <div className='flex-1 bg-ui-cream overflow-hidden flex flex-col'>
      {/* Header */}
      <div className='bg-white border-b border-ui-border px-4 sm:px-6 pt-14 sm:pt-4 sm:py-4 pb-3 sm:pb-4 flex-shrink-0'>
        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
          <div>
            <h1 className='text-lg sm:text-xl font-semibold text-gray-900'>
              {t('llmConfig.title')}
            </h1>
            <p className='text-sm text-gray-600 mt-1'>{t('llmConfig.subtitle')}</p>
          </div>
          <button
            onClick={handleAddProvider}
            className='flex items-center justify-center space-x-2 bg-ui-primary text-white px-4 py-2 rounded-lg hover:bg-ui-accent transition-colors w-full sm:w-auto'
          >
            <Plus size={18} />
            <span>{t('llmConfig.addProvider')}</span>
          </button>
        </div>
      </div>

      <div className='px-4 sm:p-6 space-y-4 sm:space-y-6 flex-1 overflow-y-auto'>
        {/* Statistics Cards */}
        <div className='grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4'>
          <StatsCard
            label={t('llmConfig.totalApis')}
            value={stats.total}
            icon={<Settings size={16} />}
          />
          <StatsCard
            label={t('llmConfig.active')}
            value={stats.active}
            icon={<CheckCircle size={16} />}
          />
          <StatsCard
            label={t('llmConfig.default')}
            value={stats.default}
            icon={<CheckCircle size={16} />}
          />
        </div>

        {/* Provider Cards */}
        <div className='space-y-4'>
          {providers.length === 0 ? (
            <div className='bg-white border border-ui-border rounded-lg shadow-ui p-8 sm:p-12 text-center'>
              <Settings size={48} className='text-ui-muted mx-auto mb-4' />
              <h3 className='text-lg font-semibold text-ui-charcoal mb-2'>
                {t('llmConfig.noProviders')}
              </h3>
              <p className='text-sm text-ui-secondary mb-6'>
                {t('llmConfig.noProvidersDescription')}
              </p>
              <button
                onClick={handleAddProvider}
                className='inline-flex items-center space-x-2 bg-ui-primary text-white px-4 py-2 rounded-lg hover:bg-ui-accent transition-colors'
              >
                <Plus size={18} />
                <span>{t('llmConfig.addFirstProvider')}</span>
              </button>
            </div>
          ) : (
            providers.map(provider => (
              <div
                key={provider.id}
                className='bg-white border border-ui-border rounded-lg shadow-ui hover:shadow-ui-md transition-shadow'
              >
                <div className='p-4 sm:p-6'>
                  {/* Provider Header */}
                  <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4'>
                    <div className='flex items-center space-x-3 sm:space-x-4'>
                      {getStatusIcon(provider.status)}
                      <div>
                        <h3 className='font-heading-lg font-semibold text-ui-charcoal text-base sm:text-lg'>
                          {provider.name}
                        </h3>
                        <p className='font-body-sm text-ui-secondary text-xs sm:text-sm'>
                          {formatProviderName(provider.provider)} •{' '}
                          {provider.models.find(m => m.default_for?.includes('chat'))
                            ?.display_name || 'No default model'}
                        </p>
                      </div>
                    </div>
                    <span
                      className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-body-sm font-medium border ${getStatusColor(provider.status)}`}
                    >
                      {t(`llmConfig.status.${provider.status}`)} -{' '}
                      {provider.status === 'active'
                        ? t('llmConfig.statusText.healthy')
                        : provider.status === 'degraded'
                          ? t('llmConfig.statusText.highLoad')
                          : t('llmConfig.status.inactive')}
                    </span>
                  </div>

                  {/* Provider Details */}
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-4'>
                    <div>
                      <label className='font-body-sm text-ui-muted block mb-1 text-xs sm:text-sm'>
                        {t('llmConfig.apiEndpoint')}
                      </label>
                      <code
                        className='block px-2 sm:px-3 py-2 bg-gray-50 border border-ui-border rounded font-body-sm text-ui-charcoal font-mono text-xs sm:text-sm truncate overflow-hidden'
                        title={provider.endpoint}
                      >
                        {provider.endpoint}
                      </code>
                    </div>

                    <div className='flex items-end'>
                      <button
                        onClick={() => handleRotateApiKey(provider.id)}
                        className='w-full px-3 sm:px-4 py-2 text-xs sm:text-sm font-body-sm text-ui-primary border border-ui-primary rounded-lg hover:bg-ui-primary hover:text-white transition-colors flex items-center justify-center gap-2'
                      >
                        <KeyRound size={16} />
                        <span>{t('llmConfig.rotateKey')}</span>
                      </button>
                    </div>
                  </div>

                  {/* Models */}
                  <div className='mb-4'>
                    <label className='font-body-sm text-ui-muted block mb-2 text-xs sm:text-sm'>
                      {t('llmConfig.models')}
                    </label>
                    <div className='flex flex-wrap gap-2'>
                      {provider.models.map(model => (
                        <span
                          key={model.id}
                          className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-lg text-xs font-body-sm border ${
                            model.enabled
                              ? 'bg-ui-primary/10 text-ui-primary border-ui-primary/20'
                              : 'bg-gray-100 text-gray-600 border-gray-200'
                          }`}
                        >
                          {model.display_name}
                          {model.default_for && model.default_for.length > 0 && (
                            <span className='ml-1 text-ui-muted hidden sm:inline'>
                              (default: {model.default_for[0]})
                            </span>
                          )}
                          <button
                            onClick={() => handleToggleModel(provider.id, model.id)}
                            className='ml-2 hover:opacity-70'
                            title={model.enabled ? 'Disable' : 'Enable'}
                          >
                            {model.enabled ? <CheckCircle size={12} /> : <XCircle size={12} />}
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Rate Limits and Usage */}
                  <div className='grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-4'>
                    <div>
                      <label className='font-body-sm text-ui-muted block mb-1 text-xs sm:text-sm'>
                        {t('llmConfig.rateLimit')}
                      </label>
                      <p className='font-body-sm text-ui-charcoal text-sm sm:text-base'>
                        {provider.rate_limits.requests_per_minute.toLocaleString()} RPM
                      </p>
                    </div>
                    <div>
                      <label className='font-body-sm text-ui-muted block mb-1 text-xs sm:text-sm'>
                        {t('llmConfig.usageToday')}
                      </label>
                      <p className='font-body-sm text-ui-charcoal text-sm sm:text-base'>
                        {provider.usage_today.toLocaleString()} requests
                      </p>
                    </div>
                    <div>
                      <label className='font-body-sm text-ui-muted block mb-1 text-xs sm:text-sm'>
                        {t('llmConfig.lastUsed')}
                      </label>
                      <p className='font-body-sm text-ui-charcoal text-sm sm:text-base'>
                        {formatLastUsed(provider.last_used)}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className='flex flex-wrap items-center justify-end gap-2 pt-4 border-t border-ui-border'>
                    <button
                      onClick={() => handleTestConnection(provider.id)}
                      disabled={testingProviders[provider.id]}
                      className={`flex items-center space-x-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-body-sm rounded-lg transition-colors disabled:opacity-50 ${
                        testStatus[provider.id] === 'failed'
                          ? 'text-red-600 bg-red-50 hover:bg-red-100'
                          : testStatus[provider.id] === 'success'
                            ? 'text-green-600 bg-green-50 hover:bg-green-100'
                            : 'text-green-600 bg-green-50 hover:bg-green-100'
                      }`}
                    >
                      {testingProviders[provider.id] ? (
                        <Loader2 size={14} className='animate-spin' />
                      ) : testStatus[provider.id] === 'failed' ? (
                        <XCircle size={14} />
                      ) : testStatus[provider.id] === 'success' ? (
                        <CheckCircle size={14} />
                      ) : (
                        <Play size={14} />
                      )}
                      <span>
                        {testStatus[provider.id] === 'failed'
                          ? t('llmConfig.connectionFailed')
                          : testStatus[provider.id] === 'success'
                            ? t('llmConfig.connectionSuccess', {
                                latency: testLatency[provider.id] || 0,
                              })
                            : 'Test Connection'}
                      </span>
                    </button>
                    <button
                      onClick={() => handleEditProvider(provider)}
                      className='flex items-center space-x-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-body-sm text-ui-charcoal hover:bg-ui-light rounded-lg transition-colors'
                    >
                      <Edit size={14} />
                      <span>{t('llmConfig.edit')}</span>
                    </button>
                    <button
                      onClick={() => handleDeleteProvider(provider)}
                      className='flex items-center space-x-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-body-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors'
                    >
                      <Trash2 size={14} />
                      <span>{t('llmConfig.delete')}</span>
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Add New Provider Button - only show if there are providers */}
        {providers.length > 0 && (
          <button
            onClick={handleAddProvider}
            className='w-full py-4 border-2 border-dashed border-ui-border rounded-lg text-ui-muted hover:border-ui-primary hover:text-ui-primary transition-colors font-body-sm'
          >
            <Plus size={20} className='inline mr-2' />
            {t('llmConfig.addNewProvider')}
          </button>
        )}
      </div>

      {/* Provider Form Dialog */}
      <LLMProviderDialog
        open={providerDialogOpen}
        onClose={() => setProviderDialogOpen(false)}
        onSave={handleSaveProvider}
        provider={selectedProvider}
        mode={providerDialogMode}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setProviderToDelete(null);
        }}
        onConfirm={confirmDeleteProvider}
        title={t('llmConfig.dialog.deleteConfirmTitle')}
        message={t('llmConfig.dialog.deleteConfirmMessage', { name: providerToDelete?.name || '' })}
        confirmText={t('llmConfig.dialog.deleteConfirm')}
        cancelText={t('common.cancel')}
        variant='danger'
        loading={saving}
      />

      {/* Rotate Key Dialog */}
      <RotateKeyDialog
        open={rotateConfirmOpen}
        onClose={() => {
          setRotateConfirmOpen(false);
          setProviderToRotate(null);
        }}
        onConfirm={confirmRotateApiKey}
        providerName={providerToRotate?.name || ''}
        loading={saving}
      />
    </div>
  );
};

interface StatsCardProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
}

const StatsCard: React.FC<StatsCardProps> = ({ label, value, icon }) => {
  return (
    <div className='bg-white border border-ui-border rounded-lg shadow-ui p-3 sm:p-4 md:p-6'>
      <div className='flex items-center space-x-3'>
        <div className='w-8 h-8 sm:w-10 sm:h-10 bg-blue-50 rounded-lg flex items-center justify-center text-ui-primary'>
          {icon}
        </div>
        <div>
          <div className='font-display-lg font-bold text-ui-charcoal text-base sm:text-xl md:text-2xl'>
            {value}
          </div>
          <div className='font-body-sm text-ui-muted text-xs sm:text-sm'>{label}</div>
        </div>
      </div>
    </div>
  );
};

export default LLMConfigPage;
